/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    Suspense,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { Await, useLocation, useMatches } from 'react-router';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { SideMenuLinksPageRegion } from '@/components/navigation-menu-mega/side-menu-links-page-region';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { useSubCategory } from '@/components/navigation-menu/context';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { routes, routeHref } from '@/route-paths';

const DESKTOP_MEGA_MENU_TOGGLE_SELECTOR = '[data-desktop-mega-menu-toggle]';
const ACTIVE_TOP_CATEGORY_STORAGE_KEY = 'vf-active-top-menu-category-id';

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={routeHref(routes.category, { categoryId: category.id })}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return typeof category.onlineSubCategoriesCount === 'number' && category.onlineSubCategoriesCount > 0;
}

function getCategoryIdFromPathname(pathname: string): string | undefined {
    const match = pathname.match(/\/category\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function getStoredTopCategoryId(categories: ShopperProducts.schemas['Category'][]): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }

    try {
        const categoryId = window.sessionStorage.getItem(ACTIVE_TOP_CATEGORY_STORAGE_KEY);
        return categories.some((category) => category.id === categoryId && hasSubcategories(category))
            ? categoryId ?? undefined
            : undefined;
    } catch {
        return undefined;
    }
}

function setStoredTopCategoryId(categoryId: string | undefined): void {
    if (typeof window === 'undefined' || !categoryId) {
        return;
    }

    try {
        window.sessionStorage.setItem(ACTIVE_TOP_CATEGORY_STORAGE_KEY, categoryId);
    } catch {
        // Ignore unavailable storage.
    }
}

function DrilldownMenuLevel({
    category: rawCategory,
    topCategoryId,
    showBack,
    onBack,
    onOpenCategory,
    onClose,
    viewAllLabel,
}: {
    category: ShopperProducts.schemas['Category'];
    topCategoryId: string;
    showBack?: boolean;
    onBack: () => void;
    onOpenCategory: (category: ShopperProducts.schemas['Category']) => void;
    onClose: () => void;
    viewAllLabel: string;
}): ReactElement {
    const category = useSubCategory(rawCategory.id) ?? rawCategory;
    const subCategories = category.categories ?? [];

    return (
        <>
            {showBack && (
                <button
                    type="button"
                    onClick={onBack}
                    className="mb-6 flex items-center gap-1 px-0 py-2 text-left text-base font-medium !text-white hover:opacity-70 transition-opacity">
                    <ChevronLeft className="size-5 !text-white" />
                    {category.name}
                </button>
            )}
            <NavLink
                to={`/category/${category.id}`}
                onClick={() => {
                    setStoredTopCategoryId(topCategoryId);
                    onClose();
                }}
                className="group/navigation-menu-link flex flex-col gap-1 rounded-sm p-2 text-base font-regular transition-all outline-none hover:!bg-transparent focus:!bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white">
                <span className="relative w-fit self-start origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/navigation-menu-link:-rotate-3 group-hover/navigation-menu-link:after:scale-x-100">
                    {viewAllLabel}
                </span>
            </NavLink>
            <ul
                style={{
                    width: '100%',
                    maxWidth: '100%',
                }}
                className="flex flex-col gap-0 p-0">
                {subCategories.map((subCategory) => (
                    <li key={subCategory.id}>
                        {hasSubcategories(subCategory) ? (
                            <button
                                type="button"
                                onClick={() => onOpenCategory(subCategory)}
                                className="group/navigation-menu-link flex w-full items-center justify-between gap-3 rounded-sm p-2 text-left text-base font-regular transition-all outline-none hover:!bg-transparent focus:!bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white">
                                <span className="relative w-fit self-start origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/navigation-menu-link:-rotate-3 group-hover/navigation-menu-link:after:scale-x-100">
                                    {subCategory.name}
                                </span>
                                <ChevronRight className="size-5 shrink-0 !text-white" />
                            </button>
                        ) : (
                            <NavLink
                                to={`/category/${subCategory.id}`}
                                onClick={() => {
                                    setStoredTopCategoryId(topCategoryId);
                                    onClose();
                                }}
                                className="group/navigation-menu-link flex flex-col gap-1 rounded-sm p-2 text-base font-regular transition-all outline-none hover:!bg-transparent focus:!bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white">
                                <span className="relative w-fit self-start origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/navigation-menu-link:-rotate-3 group-hover/navigation-menu-link:after:scale-x-100">
                                    {subCategory.name}
                                </span>
                            </NavLink>
                        )}
                    </li>
                ))}
            </ul>
        </>
    );
}

function MobileMenuCategory({
    category: rawCategory,
    expandedCategories,
    onToggle,
    onNavigate,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedCategories: Set<string>;
    onToggle: (categoryId: string) => void;
    onNavigate: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    const enrichedCategory = useSubCategory(rawCategory.id);
    const category = enrichedCategory ?? rawCategory;
    const hasChildren = hasSubcategories(category);
    const isExpanded = expandedCategories.has(category.id);

    const renderSubcategoryLinks = (
        subcategories: ShopperProducts.schemas['Category'][] | undefined,
        level = 1
    ): ReactElement[] =>
        subcategories?.map((subcategory) => (
            <li key={subcategory.id}>
                <NavLink
                    to={routeHref(routes.category, { categoryId: subcategory.id })}
                    onClick={onNavigate}
                    className={cn(
                        'block py-2 text-sm font-medium hover:opacity-70 transition-opacity',
                        level > 1 && 'text-header-foreground/80'
                    )}>
                    {subcategory.name}
                </NavLink>
                {subcategory.categories?.length ? (
                    <ul className="pl-4 space-y-1">{renderSubcategoryLinks(subcategory.categories, level + 1)}</ul>
                ) : null}
            </li>
        )) ?? [];

    return (
        <li>
            <div className="flex items-center justify-between">
                <NavLink
                    to={routeHref(routes.category, { categoryId: category.id })}
                    onClick={onNavigate}
                    className="flex-1 py-3 text-base font-medium hover:opacity-70 transition-opacity">
                    {category.name}
                </NavLink>

                {hasChildren && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggle(category.id)}
                        className="ml-2 p-2 h-auto shrink-0 hover:bg-transparent hover:opacity-50 transition-opacity"
                        aria-label={
                            isExpanded
                                ? t('collapseCategory', {
                                      category: category.name,
                                      defaultValue: `Collapse ${category.name}`,
                                  })
                                : t('expandCategory', {
                                      category: category.name,
                                      defaultValue: `Expand ${category.name}`,
                                  })
                        }
                        aria-expanded={isExpanded}>
                        <ChevronDown
                            className={cn('size-5 transition-transform duration-200', {
                                'rotate-180': isExpanded,
                            })}
                        />
                    </Button>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className="pl-4 pb-2 space-y-1 border-l border-header-foreground/10">
                    {renderSubcategoryLinks(category.categories)}
                </ul>
            )}
        </li>
    );
}

/**
 * MobileMenuDropdown - Renders the mobile menu dropdown content with expandable subcategories
 * Uses absolute positioning (relative to header) to automatically appear below the header
 * regardless of header height changes. No hardcoded values needed.
 */
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const { t } = useTranslation('header');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    // Mount the category list only while the menu is open. Each MobileMenuCategory subscribes to the sub-category
    // store via useSubCategory (useSyncExternalStore). Keeping that subtree mounted while the menu is closed puts the
    // subscribers in the SSR/hydration tree, so the post-hydration store fill re-renders the header and cascades into
    // a whole-page flicker. Gating the mount on `isOpen` keeps the subscribers out of the initial render — matching
    // the desktop mega panel, whose subscribers live inside the lazily-mounted Radix content.
    if (!context || !context.isOpen) {
        return null;
    }

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <div className="lg:hidden absolute left-0 right-0 top-full bg-header-background text-header-foreground shadow-lg z-40 max-h-[70vh] overflow-y-auto">
            <nav className="px-4 py-4" aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                <ul className="space-y-1">
                    {context.categories.map((category) => (
                        <MobileMenuCategory
                            key={category.id}
                            category={category}
                            expandedCategories={expandedCategories}
                            onToggle={toggleCategory}
                            onNavigate={context.close}
                        />
                    ))}
                </ul>
            </nav>
        </div>
    );
}

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
interface ResponsiveNavigationMenuProps extends ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> {
    /** Side Menu Links Page from the `_app` loader (page ID `side-menu-links`). */
    sideMenuLinksPage?: Promise<PageWithComponentData | null>;
}

type ResponsiveNavigationMenuInnerProps = Omit<ResponsiveNavigationMenuProps, 'sideMenuLinksPage'> & {
    sideMenuLinksPage?: PageWithComponentData | null;
};

function ResponsiveNavigationMenuInner({
    resolve,
    defer,
    sideMenuLinksPage,
}: ResponsiveNavigationMenuInnerProps): ReactElement {
    const [desktopMenuValue, setDesktopMenuValue] = useState('');
    const [drilldownPath, setDrilldownPath] = useState<ShopperProducts.schemas['Category'][]>([]);
    const desktopMenuRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const location = useLocation();
    const matches = useMatches();
    const isHomePage = matches.some((match) => match.id === 'routes/_app._index');

    const closeSideMenu = useCallback(() => {
        setDesktopMenuValue('');
        setDrilldownPath([]);
    }, []);

    const defaultListStyle = {
        width: '100%',
        maxWidth: '100%',
    };

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (categoryId: string) => {
            void navigate(routeHref(routes.category, { categoryId }));
        },
        [navigate]
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
            path,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
            path: ReadonlyArray<ShopperProducts.schemas['Category']>;
        }) => {
            const isSubcategory = level >= 1;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            return {
                className: cn(
                    'text-base font-regular',
                    isSubcategory &&
                        'hover:!bg-transparent focus:!bg-transparent [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white transition-colors'
                ),
                ...(isSubcategory && {
                    onClick: () => {
                        setStoredTopCategoryId(path[0]?.id);
                        setDesktopMenuValue('');
                        setDrilldownPath([]);
                    },
                }),
                ...(isClickableParent && {
                    // Use onPointerDown instead of onClick for mouse-only navigation.
                    // This preserves keyboard accessibility: Enter/Space on the trigger
                    // expands the dropdown (Radix behavior), while mouse clicks navigate
                    // to the category page. Without this guard, keyboard users would be
                    // forced to navigate without being able to explore subcategories.
                    onPointerDown: (e: React.PointerEvent) => {
                        if (e.pointerType === 'mouse') {
                            handleTopLevelClick(category.id);
                        }
                    },
                }),
            };
        },
        [handleTopLevelClick]
    );

    const renderMenuElement = useCallback(
        ({
            level,
            category,
            path,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            path: ReadonlyArray<ShopperProducts.schemas['Category']>;
        }) => {
            if (level === 0) {
                return category.name;
            }

            if (!hasSubcategories(category)) {
                return undefined;
            }

            return (
                <button
                    type="button"
                    onClick={() => {
                        setStoredTopCategoryId(path[0]?.id);
                        setDrilldownPath((currentPath) => [...currentPath, category]);
                    }}
                    className="group/navigation-menu-link flex w-full items-center justify-between gap-3 rounded-sm p-2 text-left text-base font-regular transition-all outline-none hover:!bg-transparent focus:!bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white">
                    <span className="relative w-fit self-start origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/navigation-menu-link:-rotate-3 group-hover/navigation-menu-link:after:scale-x-100">
                        {category.name}
                    </span>
                    <ChevronRight className="size-5 shrink-0 !text-white" />
                </button>
            );
        },
        []
    );

    useEffect(() => {
        if (!desktopMenuValue) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest(DESKTOP_MEGA_MENU_TOGGLE_SELECTOR)) return;
            if (desktopMenuRef.current?.contains(target)) return;

            setDesktopMenuValue('');
            setDrilldownPath([]);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [desktopMenuValue]);

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer}>
            {({ categories }) => {
                const firstDesktopMenuCategoryId = categories.find(hasSubcategories)?.id ?? categories[0]?.id ?? '';
                const currentCategoryId = getCategoryIdFromPathname(location.pathname);
                const currentTopMenuCategoryId = categories.find(
                    (category) => category.id === currentCategoryId && hasSubcategories(category)
                )?.id;
                const storedTopMenuCategoryId = getStoredTopCategoryId(categories);
                const defaultDesktopMenuCategoryId =
                    isHomePage
                        ? firstDesktopMenuCategoryId
                        : (currentTopMenuCategoryId ?? storedTopMenuCategoryId ?? firstDesktopMenuCategoryId);
                const toggleDesktopMenu = () => {
                    setDesktopMenuValue((currentValue) => {
                        if (currentValue) {
                            setDrilldownPath([]);
                            return '';
                        }
                        return defaultDesktopMenuCategoryId;
                    });
                };

                return (
                    <>
                        {/* Mobile hamburger: opens the desktop mega menu layout */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleDesktopMenu}
                            data-desktop-mega-menu-toggle
                            className="hover:bg-header-menu-icon-hover-background group-data-[transparent=true]/header:hover:bg-header-transparent-menu-icon-hover-background! transition-opacity p-1! m-1!"
                            aria-label={desktopMenuValue ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                            aria-expanded={Boolean(desktopMenuValue)}>
                            <Menu className="size-5 lg:size-6 group-data-[transparent=true]/header:text-white! group-data-[transparent=true]/header:fill-white!" />
                        </Button>

                        {/* Mega menu content: desktop menu behavior reused for every viewport */}
                        <div ref={desktopMenuRef} className="flex items-center h-full">
                            <CategoryNavigationMenu
                                categories={categories}
                                delayDuration={0}
                                maxDepth={2}
                                value={desktopMenuValue}
                                onValueChange={(value) => {
                                    if (value) {
                                        setDesktopMenuValue(value);
                                    }
                                }}
                                propsViewport={() => ({
                                    'data-desktop-mega-menu-panel': true,
                                    className:
                                        '!fixed !left-0 !top-0 !w-screen !max-w-screen !bg-transparent rounded-none border-0 border-r border-header-menu-border shadow-lg lg:!w-[400px] lg:!max-w-[400px] [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none relative',
                                    style: {
                                        backdropFilter: 'blur(6px) brightness(0.25)',
                                        WebkitBackdropFilter: 'blur(6px) brightness(0.25)',
                                    },
                                })}
                                propsContentContainer={() => ({
                                    className:
                                        '!static !p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
                                })}
                                propsContent={() => ({
                                    className:
                                        'relative flex w-full flex-col px-6 py-8 !h-[100dvh] !max-h-[100dvh] !overflow-y-auto',
                                })}
                                propsList={({ parent, categories: subCategories, level }) => {
                                    if (level === 0) {
                                        return {
                                            className: 'sr-only',
                                        };
                                    }
                                    if (level === 1) {
                                        if (drilldownPath.length > 0) {
                                            return {
                                                className: 'hidden',
                                            };
                                        }
                                        if (isVertical(parent)) {
                                            return {
                                                style: defaultListStyle,
                                                className: 'flex flex-col gap-0 p-0',
                                            };
                                        }
                                        return {
                                            style: {
                                                ...defaultListStyle,
                                                gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                                            },
                                            className: 'grid p-0',
                                        };
                                    }
                                }}
                                propsElement={getElementProps}
                                renderElement={renderMenuElement}
                                renderSlotListBefore={({ level, parent }) => {
                                    if (level === 1) {
                                        const activeDrilldownCategory = drilldownPath[drilldownPath.length - 1];

                                        return (
                                            <>
                                                {!activeDrilldownCategory && (
                                                    <nav
                                                        className="mb-6 pr-14"
                                                        aria-label={t('mainNavigation', 'Main navigation')}>
                                                        <ul className="flex flex-row gap-1">
                                                            {categories.map((topCategory) => {
                                                                const isActive = topCategory.id === parent?.id;
                                                                const hasChildren = hasSubcategories(topCategory);
                                                                const className = cn(
                                                                    'block w-full px-0 py-2 text-left text-base font-medium'
                                                                );

                                                                return (
                                                                    <li key={topCategory.id}>
                                                                        {hasChildren ? (
                                                                            <button
                                                                                type="button"
                                                                                className={cn(
                                                                                    className,
                                                                                    '!text-white px-1 py-0 leading-[1.28] border border-transparent hover:border-white rounded-xs transition-all',
                                                                                    isActive &&
                                                                                        'bg-white !text-black border-white'
                                                                                )}
                                                                                onClick={() => {
                                                                                    setStoredTopCategoryId(topCategory.id);
                                                                                    setDrilldownPath([]);
                                                                                    setDesktopMenuValue(topCategory.id);
                                                                                }}
                                                                                aria-current={isActive ? 'page' : undefined}>
                                                                                {topCategory.name}
                                                                            </button>
                                                                        ) : (
                                                                            <NavLink
                                                                                to={`/category/${topCategory.id}`}
                                                                                className={cn(
                                                                                    className,
                                                                                    '!text-white px-1 py-0 leading-[1.28] border border-transparent hover:border-white rounded-xs transition-all',
                                                                                    isActive &&
                                                                                        'bg-white !text-black border-white'
                                                                                )}
                                                                                onClick={() => setDesktopMenuValue('')}
                                                                                aria-current={isActive ? 'page' : undefined}>
                                                                                {topCategory.name}
                                                                            </NavLink>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </nav>
                                                )}
                                                {parent && activeDrilldownCategory ? (
                                                    <DrilldownMenuLevel
                                                        category={activeDrilldownCategory}
                                                        topCategoryId={parent.id}
                                                        showBack
                                                        onBack={() =>
                                                            setDrilldownPath((currentPath) => currentPath.slice(0, -1))
                                                        }
                                                        onOpenCategory={(category) =>
                                                            setDrilldownPath((currentPath) => [...currentPath, category])
                                                        }
                                                        onClose={() => {
                                                            setDesktopMenuValue('');
                                                            setDrilldownPath([]);
                                                        }}
                                                        viewAllLabel={t('viewAll', 'View All')}
                                                    />
                                                ) : parent ? (
                                                    <NavLink
                                                        to={`/category/${parent.id}`}
                                                        onClick={() => {
                                                            setStoredTopCategoryId(parent.id);
                                                            setDesktopMenuValue('');
                                                            setDrilldownPath([]);
                                                        }}
                                                        className="group/navigation-menu-link flex flex-col gap-1 rounded-sm p-2 text-base font-regular transition-all outline-none hover:!bg-transparent focus:!bg-transparent focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-1 [&_span]:!text-white hover:[&_span]:!text-white focus:[&_span]:!text-white">
                                                        <span className="relative w-fit self-start origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/navigation-menu-link:-rotate-3 group-hover/navigation-menu-link:after:scale-x-100">
                                                            {t('viewAll', 'View All')}
                                                        </span>
                                                    </NavLink>
                                                ) : null}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setDesktopMenuValue('');
                                                        setDrilldownPath([]);
                                                    }}
                                                    className="absolute right-4 bottom-0 top-auto z-50 m-8 p-4 h-12 w-12 !rounded-xs"
                                                    aria-label={t('closeMenu', 'Close menu')}>
                                                    <X className="size-8" />
                                                </Button>
                                            </>
                                        );
                                    }
                                }}
                                renderSlotListAfter={({ level, parent }) => {
                                    if (level !== 1 || !parent) {
                                        return null;
                                    }

                                    return (
                                        <>
                                            {!drilldownPath.length && hasBanner(parent) ? (
                                                <aside className="self-stretch">
                                                    <CategoryBanner category={parent} onClick={closeSideMenu} />
                                                </aside>
                                            ) : null}
                                            <SideMenuLinksPageRegion
                                                page={sideMenuLinksPage}
                                                onNavigate={closeSideMenu}
                                                data-testid="side-menu-links-pd-region"
                                                className="shrink-0"
                                            />
                                        </>
                                    );
                                }}
                            />
                        </div>
                    </>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}

/** Resolves side menu PD content while the nav shell is mounted, not when the panel opens. */
export default function ResponsiveNavigationMenu({
    sideMenuLinksPage,
    ...props
}: ResponsiveNavigationMenuProps): ReactElement {
    if (!sideMenuLinksPage) {
        return <ResponsiveNavigationMenuInner {...props} sideMenuLinksPage={undefined} />;
    }

    return (
        <Suspense
            fallback={<ResponsiveNavigationMenuInner {...props} sideMenuLinksPage={undefined} />}>
            <Await resolve={sideMenuLinksPage}>
                {(resolved) => (
                    <ResponsiveNavigationMenuInner {...props} sideMenuLinksPage={resolved ?? undefined} />
                )}
            </Await>
        </Suspense>
    );
}
